/* eslint-disable react-hooks/set-state-in-effect */
/**
 * PackshotPickerDialog - Auswahl des KI-Referenz-Packshots pro Produkt
 *
 * Öffnet sich aus der ProductBulkActionBar. Zeigt pro markiertem Produkt
 * alle analysierten Bilder mit Scores. Der User wählt pro Produkt das
 * beste Bild und setzt alle auf einmal als Packshot.
 */

import { useState, useEffect } from 'react'
import { Package } from 'lucide-react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/v2/ui'
import { usePackshotStatuses, useSetPackshot } from '@/hooks/useImageIntelligence'
import { ProductPackshotSection } from './ProductPackshotSection'

export interface ProductMeta {
  name: string
  sku: string
  thumbnailUrl?: string
}

export interface PackshotPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productIds: string[]
  productMeta?: Record<string, ProductMeta>
  channelId?: string
  onAnalyzeRequest?: () => void
}

export function PackshotPickerDialog({
  open, onOpenChange, productIds, productMeta, channelId, onAnalyzeRequest,
}: PackshotPickerDialogProps) {
  const { data: statuses } = usePackshotStatuses(open ? productIds : [], channelId)
  const setPackshotMutation = useSetPackshot()
  const [selections, setSelections] = useState<Record<string, string | null>>({})

  // Stabiler Key verhindert Infinite Loop durch neue Array-Referenzen bei jedem Render
  const productIdsKey = productIds.join(',')

  // Vorbelegen sobald Statuses geladen sind
   
  useEffect(() => {
    if (!statuses) return
    setSelections(prev => {
      const next = { ...prev }
      productIdsKey.split(',').filter(Boolean).forEach(id => {
        if (next[id] === undefined) {
          next[id] = statuses.get(id)?.best_candidate?.asset_id ?? null
        }
      })
      return next
    })
  }, [statuses, productIdsKey])

  const selectedCount = Object.values(selections).filter(Boolean).length
  const isPending = setPackshotMutation.isPending

  const handleSubmit = async (): Promise<void> => {
    const tasks = Object.entries(selections)
      .filter(([, assetId]) => !!assetId)
      .map(([productId, assetId]) =>
        setPackshotMutation.mutateAsync({ productId, assetId: assetId! })
      )
    await Promise.all(tasks)
    onOpenChange(false)
  }

  // Nochmaliger Klick auf ausgewähltes Bild → deselektiert (Produkt bleibt ohne Auswahl)
  const handleSelect = (productId: string, assetId: string): void => {
    setSelections(prev => ({ ...prev, [productId]: prev[productId] === assetId ? null : assetId }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="packshot-picker-dialog sm:max-w-xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary-500" aria-hidden="true" />
            <DialogTitle>Packshot auswählen</DialogTitle>
          </div>
          <DialogDescription>
            {productIds.length} Produkt{productIds.length !== 1 ? 'e' : ''} · Klicke das beste Bild als KI-Referenz-Packshot
          </DialogDescription>
        </DialogHeader>

        <PackshotProductList
          productIds={productIds}
          productMeta={productMeta}
          statuses={statuses}
          selections={selections}
          onSelect={handleSelect}
          onAnalyzeRequest={onAnalyzeRequest}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={selectedCount === 0 || isPending}
            aria-live="polite"
          >
            {isPending ? 'Wird gesetzt…' : `✓ Packshot setzen (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PackshotProductList({ productIds, productMeta, statuses, selections, onSelect, onAnalyzeRequest }: {
  productIds: string[]
  productMeta?: Record<string, ProductMeta>
  statuses: Map<string, import('@/lib/api/image-intelligence').ProductPackshotStatus> | undefined
  selections: Record<string, string | null>
  onSelect: (productId: string, assetId: string) => void
  onAnalyzeRequest?: () => void
}) {
  return (
    <div className="packshot-picker-list overflow-y-auto flex-1 space-y-3 py-1 pr-1">
      {productIds.map(id => (
        <ProductPackshotSection
          key={id}
          productId={id}
          name={productMeta?.[id]?.name ?? id}
          sku={productMeta?.[id]?.sku ?? '—'}
          thumbnailUrl={productMeta?.[id]?.thumbnailUrl}
          status={statuses?.get(id) ?? null}
          selectedAssetId={selections[id] ?? null}
          onSelect={assetId => onSelect(id, assetId)}
          onStartAnalysis={onAnalyzeRequest}
        />
      ))}
    </div>
  )
}
